import Head from 'next/head';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import { NextPageWithLayout } from '@/pages/_app';
import React, { useMemo } from 'react';
import Layout from '@/index-layouts';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { getTdkApi } from '@/lib/service/tdk';
import { getPageContentContentApi } from '@/lib/service/home';
import { page3JsonLd } from '@/lib/common/jsonld';
import { removeLastPathSegment, hyphenatedToTitleCase } from '@/shared/path-helper';
const AlternativesContent = dynamic(() => import('@/components/alternatives-alternatices'), {
  ssr: true,
});

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59');

  const { locale = 'en', resolvedUrl } = context;
  const localeRes = await serverSideTranslations(locale, ['common']);
  const parentPath = removeLastPathSegment(resolvedUrl);
  const parentTitle = hyphenatedToTitleCase(parentPath);

  const tdkData = await getTdkApi(`/alternatives/${context.params?.platform}`, locale);
  const res = await getPageContentContentApi(
    `/alternatives/${context.params?.platform}`,
    locale,
    Number(context.query.preview) || '',
    Number(context.query.preview_id) || '',
  );
  if (res.code != 0) {
    return {
      redirect: {
        destination: `/alternatives/${context.params?.platform}`,
        permanent: false,
      },
    };
  }
  let currentURL = `${process.env.NEXT_APP_WEB_HOST}`;
  if (locale != 'en') {
    currentURL = `${process.env.NEXT_APP_WEB_HOST}/${locale}/${context.params?.platform}`;
  }
  const parentUrl = `${process.env.NEXT_APP_WEB_HOST}/${locale}/${parentPath}`;

  const siteUrl =
    locale !== 'en' ? `https://${process.env.NEXT_APP_WEB_HOST}/${locale}` : `https://${process.env.NEXT_APP_WEB_HOST}`;
  const title = tdkData?.data?.title;
  const desc = tdkData?.data?.description;
  const keywords = tdkData?.data?.keywords;
  const jsonLd = page3JsonLd(
    {
      title: title,
      desc: desc,
      url: currentURL,
    },
    {
      title: parentTitle,
      url: parentUrl,
    },
    {
      url: siteUrl,
    },
  );
  return {
    props: {
      ...localeRes,
      herf: currentURL,
      locale,
      // revalidate: 60,
      data: res.data,
      title: title,
      description: desc,
      keywords: keywords,
      content: context.params?.platform,
      jsonLd,
    },
  };
};

const Home: NextPageWithLayout<InferGetServerSidePropsType<typeof getServerSideProps>> = (props: any) => {
  return useMemo(() => <AlternativesContent data={props.data} />, [props]);
};
Home.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;
  const keywords = page?.props?.keywords;
  const jsonLd = page?.props?.jsonLd;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />
          <meta name="keywords" content={keywords} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta
            property="og:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta
            property="og:image:secure_url"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta
            name="twitter:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
          />
          <link
            rel="alternate"
            hrefLang="en"
            href={`${process.env.NEXT_APP_WEB_HOST}/en/alternatives/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="zh"
            href={`${process.env.NEXT_APP_WEB_HOST}/zh/alternatives/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="tw"
            href={`${process.env.NEXT_APP_WEB_HOST}/tw/alternatives/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="ja"
            href={`${process.env.NEXT_APP_WEB_HOST}/ja/alternatives/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="ko"
            href={`${process.env.NEXT_APP_WEB_HOST}/ko/alternatives/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="pt"
            href={`${process.env.NEXT_APP_WEB_HOST}/pt/alternatives/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="de"
            href={`${process.env.NEXT_APP_WEB_HOST}/de/alternatives/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="es"
            href={`${process.env.NEXT_APP_WEB_HOST}/es/alternatives/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="it"
            href={`${process.env.NEXT_APP_WEB_HOST}/it/alternatives/${page.props.content}`}
          />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {jsonLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default Home;
